import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { gql, useMutation } from '@apollo/client';
import '@livekit/components-styles';
import '@livekit/components-styles/prefabs';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BtwLink, Button, CtaMessage, FullScreenLoader, NotFound, UnauthorizedAccess, UnhandledError, } from '@marvelapp/ballpark-components';
import { WarningIcon } from '@marvelapp/ballpark-icons';
import { isDebugMode } from '@marvelapp/core';
import { JoinRoomErrorEnum } from '../../__generated__/queryTypes';
import { SessionEnded } from '../../components/Record/SessionEnded';
import { LiveRoom } from './LiveRoom';
import Record from './Record';
import { Resume, clearResponseInStorage } from './Resume';
import { RoomMetaContext } from './RoomMetaContext';
const JOIN_ROOM = gql `
  mutation joinRoom($roomName: String!, $liveRoleType: LiveRoleEnum!) {
    joinRoom(roomName: $roomName, liveRoleType: $liveRoleType) {
      token
      ok
      responseUUID
      egressStartTime
      isAlreadyInRoom
      error {
        code
        message
      }
    }
  }
`;
const SessionConfirmation = observer(function SessionConfirmation({ onConfirm, }) {
    return (_jsx("div", { className: "flex min-h-screen w-full items-center justify-center", children: _jsxs(CtaMessage.Root, { children: [_jsxs(CtaMessage.Header, { icon: _jsx(WarningIcon, {}), children: [_jsx(CtaMessage.Heading, { children: "You're already in a Ballpark Live session" }), _jsx(CtaMessage.Description, { children: "You have an active session in another window or device. Continuing here will end your other session. To cancel, simply close this window." })] }), _jsx(CtaMessage.Actions, { children: _jsx(Button, { variant: "primary", onClick: onConfirm, children: "Continue Here" }) })] }) }));
});
export const JoinRoom = observer(function JoinRoom() {
    const params = useParams();
    const [initiated, setInitiated] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(true);
    const roomName = params.roomId;
    const [joinRoom, { data, loading, error }] = useMutation(JOIN_ROOM, {
        variables: {
            roomName,
            liveRoleType: params.role.toLocaleUpperCase(),
        },
    });
    useEffect(() => {
        joinRoom();
        setInitiated(true);
    }, [joinRoom]);
    const restart = useCallback(() => {
        clearResponseInStorage(params.shareUUID, true);
        window.location.href = `/share/live/${params.role}/${params.shareUUID}`;
    }, [params.role, params.shareUUID]);
    useEffect(() => {
        if (!isDebugMode()) {
            return;
        }
        window.restartSession = restart;
        return () => {
            window.restartSession = undefined;
        };
    }, [restart]);
    const handleConfirmSession = () => {
        setShowConfirmation(false);
    };
    if (!initiated || loading) {
        return _jsx(FullScreenLoader, {});
    }
    if (error || !data) {
        return _jsx(UnhandledError, {});
    }
    if (data.joinRoom.isAlreadyInRoom && showConfirmation) {
        return _jsx(SessionConfirmation, { onConfirm: handleConfirmSession });
    }
    if (data.joinRoom.error) {
        if (data.joinRoom.error.code === JoinRoomErrorEnum.ROOM_NOT_FOUND) {
            return (_jsx(NotFound, { heading: "Session not found", subheading: "The session you're looking for could have been deleted or never existed." }));
        }
        if (data.joinRoom.error.code === JoinRoomErrorEnum.ROOM_CLOSED) {
            return (_jsx("div", { className: "flex min-h-screen w-full items-center justify-center", children: _jsx(SessionEnded, {}) }));
        }
        if (data.joinRoom.error.code === JoinRoomErrorEnum.PERMISSION_DENIED) {
            // Currently PERMISSION_DENIED is only returned for observer role on a
            // non-enterprise plan so we can provide a more specific error message.
            return (_jsx(UnauthorizedAccess, { heading: "You don't have permission to join as an observer", subheading: _jsxs(_Fragment, { children: ["The observer view is only available for Enterprise customers. Please", ' ', _jsx(BtwLink, { href: "https://ballparkhq.com/contact/sales", children: "contact us" }), ' ', "to enquire about Ballpark Enterprise."] }) }));
        }
        return _jsx(UnhandledError, {});
    }
    if (params.role === 'participant') {
        return (_jsx(RoomMetaContext.Provider, { value: data.joinRoom, children: _jsx(Record
            // TODO implement partner data flow
            , { 
                // TODO implement partner data flow
                partnerData: null, responseUUID: data.joinRoom.responseUUID, shareUUID: params.shareUUID, restart: restart, children: _jsx(Resume, { isResuming: false, onRestart: restart }) }) }));
    }
    return (_jsx(RoomMetaContext.Provider, { value: data.joinRoom, children: _jsx("div", { className: "h-full", children: _jsx(LiveRoom, { token: data.joinRoom.token }) }) }));
});
